/**
 * AdminCouponsPage — RSS_Admin_Coupon_Management_Page spec.
 *
 * Surfaces every coupon in the system with admin CRUD + filter by status/type.
 * Form covers: code, discount type/value, validity window, all 4 usage caps
 * (total / per-family / per-learner / per-session), min order amount,
 * scope filters (locations + camps), active toggle, notes.
 *
 * Backend: /api/coupons/ (admin CRUD) + /api/coupons/validate/ (public,
 * used by the cart for real-time feedback).
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Tag, Plus, Pencil, Trash2, Loader2, X, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-gray-200 text-gray-700',
  scheduled: 'bg-blue-100 text-blue-700',
  expired: 'bg-rose-100 text-rose-700',
  exhausted: 'bg-amber-100 text-amber-800',
};

const EMPTY_FORM = {
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  start_date: '',
  end_date: '',
  total_uses_limit: '',
  per_family_limit: '',
  per_learner_limit: '',
  per_session_limit: '',
  min_order_amount: '',
  locations: [],
  camps: [],
  active: true,
  notes: '',
};

export default function AdminCouponsPage() {
  const { api } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [camps, setCamps] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | 'new' | coupon
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, cm] = await Promise.allSettled([
        api.get('/coupons/?page_size=200'),
        api.get('/locations/?page_size=200'),
        api.get('/camps/?active_only=false&page_size=100'),
      ]);
      setCoupons(c.status === 'fulfilled' ? (c.value.data.results || []) : []);
      setLocations(l.status === 'fulfilled' ? (l.value.data.results || []) : []);
      setCamps(cm.status === 'fulfilled' ? (cm.value.data.results || []) : []);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };
  const openEdit = (c) => {
    setForm({
      code: c.code || '',
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: c.discount_value ?? '',
      start_date: c.start_date || '',
      end_date: c.end_date || '',
      total_uses_limit: c.total_uses_limit ?? '',
      per_family_limit: c.per_family_limit ?? '',
      per_learner_limit: c.per_learner_limit ?? '',
      per_session_limit: c.per_session_limit ?? '',
      min_order_amount: c.min_order_amount ?? '',
      locations: c.locations || [],
      camps: c.camps || [],
      active: c.active,
      notes: c.notes || '',
    });
    setEditing(c);
  };
  const closeForm = () => { setEditing(null); setForm(EMPTY_FORM); };

  const onSave = async () => {
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (!form.discount_value || Number(form.discount_value) <= 0) {
      toast.error('Discount value must be greater than 0'); return;
    }
    if (form.discount_type === 'percent' && Number(form.discount_value) > 100) {
      toast.error('Percent must be ≤ 100'); return;
    }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      discount_type: form.discount_type,
      discount_value: form.discount_value,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      total_uses_limit: form.total_uses_limit === '' ? null : Number(form.total_uses_limit),
      per_family_limit: form.per_family_limit === '' ? null : Number(form.per_family_limit),
      per_learner_limit: form.per_learner_limit === '' ? null : Number(form.per_learner_limit),
      per_session_limit: form.per_session_limit === '' ? null : Number(form.per_session_limit),
      min_order_amount: form.min_order_amount === '' ? null : form.min_order_amount,
      locations: form.locations,
      camps: form.camps,
      active: form.active,
      notes: form.notes,
    };
    try {
      if (editing === 'new') {
        await api.post('/coupons/', payload);
        toast.success(`Coupon ${payload.code} created`);
      } else {
        await api.patch(`/coupons/${editing.id}/`, payload);
        toast.success(`Coupon ${payload.code} updated`);
      }
      await fetchAll();
      closeForm();
    } catch (err) {
      const msg = err.response?.data?.code?.[0]
        || err.response?.data?.discount_value?.[0]
        || err.response?.data?.end_date?.[0]
        || err.response?.data?.detail
        || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/coupons/${deleteTarget.id}/`);
      toast.success(`Coupon ${deleteTarget.code} deleted`);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = coupons.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (typeFilter !== 'all' && c.discount_type !== typeFilter) return false;
    return true;
  });

  const summarizeLimits = (c) => {
    const parts = [];
    if (c.total_uses_limit) parts.push(`${c.total_redemptions || 0}/${c.total_uses_limit} total`);
    if (c.per_family_limit) parts.push(`${c.per_family_limit}/family`);
    if (c.per_learner_limit) parts.push(`${c.per_learner_limit}/learner`);
    if (c.per_session_limit) parts.push(`${c.per_session_limit}/session`);
    if (c.min_order_amount) parts.push(`min $${c.min_order_amount}`);
    if (c.location_names?.length) parts.push(`${c.location_names.length} loc${c.location_names.length === 1 ? '' : 's'}`);
    if (c.camp_names?.length) parts.push(`${c.camp_names.length} camp${c.camp_names.length === 1 ? '' : 's'}`);
    return parts.length ? parts.join(' · ') : 'Unlimited · all scopes';
  };

  return (
    <div className="space-y-5" data-testid="admin-coupons-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6 text-primary" /> Coupons
          </h1>
          <p className="text-sm text-muted-foreground">
            {coupons.length} total · admin-only management
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-coupon-btn">
          <Plus className="h-4 w-4 mr-1" /> Add coupon
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap">
          <div className="min-w-[160px]">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="exhausted">Exhausted</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="percent">Percentage</SelectItem>
                <SelectItem value="flat">Fixed amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Tag className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No coupons match the current filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                    <TableCell>
                      <div className="font-bold tracking-wide" data-testid={`coupon-code-${c.code}`}>{c.code}</div>
                      {c.description && <div className="text-[11px] text-muted-foreground">{c.description}</div>}
                    </TableCell>
                    <TableCell>
                      <span className="font-bold tabular-nums">
                        {c.discount_type === 'percent' ? `${Number(c.discount_value)}%` : `$${Number(c.discount_value).toFixed(2)}`}
                      </span>
                      <span className="text-[11px] text-muted-foreground ml-1">{c.discount_type === 'percent' ? 'off' : 'flat'}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {c.start_date || '—'} → {c.end_date || '—'}
                    </TableCell>
                    <TableCell className="text-xs max-w-[260px]">
                      {summarizeLimits(c)}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[c.status] || ''} data-testid={`coupon-status-${c.code}`}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.code}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteTarget(c)} data-testid={`delete-coupon-${c.code}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit modal */}
      <Dialog open={editing !== null} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add coupon' : `Edit ${editing?.code || ''}`}</DialogTitle>
            <DialogDescription>Empty limit fields mean unlimited for that dimension.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="code">Code <span className="text-destructive">*</span></Label>
                <Input id="code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                       placeholder="LOYALTY10" data-testid="form-code" disabled={editing !== 'new'} />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input id="description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                       placeholder="Short label shown to customers (e.g. Loyalty 10% off)" data-testid="form-description" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount type <span className="text-destructive">*</span></Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Fixed amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="discount_value">Value <span className="text-destructive">*</span></Label>
                <Input id="discount_value" type="number" min="0" step="0.01" value={form.discount_value}
                       onChange={e => setForm({ ...form, discount_value: e.target.value })}
                       placeholder={form.discount_type === 'percent' ? '10' : '50.00'} data-testid="form-value" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start_date" className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> Start date</Label>
                <Input id="start_date" type="date" value={form.start_date}
                       onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="form-start" />
              </div>
              <div>
                <Label htmlFor="end_date" className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" /> End date</Label>
                <Input id="end_date" type="date" value={form.end_date}
                       onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="form-end" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-sm font-semibold mb-2">Usage limits <span className="text-xs font-normal text-muted-foreground ml-1">(leave blank for unlimited)</span></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Total uses</Label>
                  <Input type="number" min="1" value={form.total_uses_limit}
                         onChange={e => setForm({ ...form, total_uses_limit: e.target.value })}
                         placeholder="∞" data-testid="form-total-limit" />
                </div>
                <div>
                  <Label className="text-xs">Per family</Label>
                  <Input type="number" min="1" value={form.per_family_limit}
                         onChange={e => setForm({ ...form, per_family_limit: e.target.value })}
                         placeholder="∞" data-testid="form-family-limit" />
                </div>
                <div>
                  <Label className="text-xs">Per learner</Label>
                  <Input type="number" min="1" value={form.per_learner_limit}
                         onChange={e => setForm({ ...form, per_learner_limit: e.target.value })}
                         placeholder="∞" data-testid="form-learner-limit" />
                </div>
                <div>
                  <Label className="text-xs">Per session</Label>
                  <Input type="number" min="1" value={form.per_session_limit}
                         onChange={e => setForm({ ...form, per_session_limit: e.target.value })}
                         placeholder="∞" data-testid="form-session-limit" />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">Min order amount ($)</Label>
                <Input type="number" min="0" step="0.01" value={form.min_order_amount}
                       onChange={e => setForm({ ...form, min_order_amount: e.target.value })}
                       placeholder="No minimum" data-testid="form-min-order" />
              </div>
            </div>

            <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Restrict to locations</Label>
                <MultiSelectChips
                  options={locations}
                  selected={form.locations}
                  onChange={(ids) => setForm({ ...form, locations: ids })}
                  testid="form-locations"
                  emptyLabel="All locations"
                />
              </div>
              <div>
                <Label className="text-xs">Restrict to camps</Label>
                <MultiSelectChips
                  options={camps}
                  selected={form.camps}
                  onChange={(ids) => setForm({ ...form, camps: ids })}
                  testid="form-camps"
                  emptyLabel="All camps"
                />
              </div>
            </div>

            <div className="border-t pt-3">
              <Label htmlFor="notes" className="text-xs">Internal notes</Label>
              <Textarea id="notes" value={form.notes} rows={2}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        placeholder="Optional notes for the team (not shown to customers)"
                        data-testid="form-notes" />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="form-active" />
              <Label className="cursor-pointer" onClick={() => setForm({ ...form, active: !form.active })}>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm} disabled={saving} data-testid="form-cancel">Cancel</Button>
            <Button onClick={onSave} disabled={saving} data-testid="form-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing === 'new' ? 'Create coupon' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete coupon?</DialogTitle>
            <DialogDescription>
              <span className="font-bold">{deleteTarget?.code}</span> will be permanently removed.
              {deleteTarget?.total_redemptions > 0 && (
                <span className="block mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  ⚠ This coupon has {deleteTarget.total_redemptions} redemption{deleteTarget.total_redemptions === 1 ? '' : 's'} on record. Past usage history will be preserved.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onDelete} data-testid="confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MultiSelectChips({ options, selected, onChange, testid, emptyLabel }) {
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div className="border rounded-md p-2 max-h-32 overflow-y-auto bg-white" data-testid={testid}>
      {options.length === 0 ? (
        <div className="text-xs text-muted-foreground">No options available.</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selected.length === 0 && <span className="text-xs text-muted-foreground italic">{emptyLabel}</span>}
          {options.map(o => {
            const isOn = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                onClick={() => toggle(o.id)}
                className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                  isOn ? 'bg-rsc-navy text-white border-rsc-navy' : 'bg-white text-muted-foreground border-gray-300 hover:border-rsc-navy hover:text-rsc-navy'
                }`}
                data-testid={`${testid}-opt-${o.id}`}
              >
                {o.name || o.code || o.id}
                {isOn && <X className="h-2.5 w-2.5 inline ml-1" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
